// Action-first page shell (WCP redesign pass, 2026-09-20).
//
// One consistent frame for every screen: title, one-line purpose, the primary
// actions grouped on the right, then the content. This is what makes the site
// predictable for the agent (controls have stable placement) and safer on a
// phone (flexible wrapping, min-width guards, no horizontal overflow).
import type { ReactNode } from 'react';

import AgentStateBadge from './AgentStateBadge';

export interface ActionBarProps {
  children: ReactNode;
  className?: string;
}

/** Primary actions for the current screen. Always wraps instead of overflowing. */
export function ActionBar({ children, className = '' }: ActionBarProps) {
  return (
    <div className={`agent-action-bar ${className}`} data-agent-region="actions">
      {children}
    </div>
  );
}

export interface PageShellProps {
  title: string;
  purpose?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Show the agent's live state next to the title. */
  showAgentState?: boolean;
  className?: string;
}

export default function PageShell({
  title,
  purpose,
  actions,
  children,
  showAgentState = true,
  className = '',
}: PageShellProps) {
  return (
    <div className={`agent-page ${className}`} data-agent-region="page">
      <header className="agent-page-header">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="agent-page-title" data-agent-id="page.title">{title}</h1>
            {showAgentState && <AgentStateBadge />}
          </div>
          {purpose && <p className="agent-page-purpose mt-0.5">{purpose}</p>}
        </div>
        {actions ? <ActionBar>{actions}</ActionBar> : null}
      </header>
      <div className="agent-page-body min-w-0">{children}</div>
    </div>
  );
}
